import type { Hex } from "viem";

import { useCallback, useState } from "react";

import type { TokenDef } from "./data/tokens.js";
import type { WalletState } from "./lib/wallet.js";
import type { BuildTxSuccessPayload, QuoteSubmission } from "./types.js";

import { CopyButton } from "./components/CopyButton.js";
import { EndpointBar } from "./components/EndpointBar.js";
import { QuoteForm } from "./components/QuoteForm.js";
import { QuoteResult } from "./components/QuoteResult.js";
import { StepsExecutor } from "./components/StepsExecutor.js";
import { TxStatusPoller } from "./components/TxStatusPoller.js";
import { WalletConnect } from "./components/WalletConnect.js";
import { useCatalog } from "./hooks/useCatalog.js";
import { BUILD_TX_QUERY, gqlFetch } from "./lib/graphql.js";
import styles from "./styles/App.module.css";

/**
 * Top-level shell. Holds:
 *
 *   - The endpoint + API key (persisted in localStorage so a page reload
 *     doesn't make you re-type the key — never stored server-side).
 *   - The latest quote result + error (mutually exclusive: one renders
 *     at a time below the form).
 *   - The wallet state (address + chainId) shared with `StepsExecutor`
 *     and the on-chain poller.
 *   - The list of broadcast tx hashes once the user signed the steps,
 *     so the `TxStatusPoller` can resume polling on remount without
 *     replaying the wallet prompts.
 *
 * No router, no Suspense, no global store. The playground is one page;
 * a flat `useState` graph is plenty for V1.1.
 */
const LS_KEYS = {
  endpoint: "swapgate.playground.endpoint",
  apiKey: "swapgate.playground.apiKey",
} as const;

// Defaults to the staging custom domain so a fresh visitor lands on a
// working endpoint without typing anything. Local dev users override it
// once with the "Local" chip and the choice is persisted in localStorage.
const DEFAULT_ENDPOINT = "https://swapgate-api-sta.tutellus.dev";

function readLS(key: string, fallback: string): string {
  try {
    return window.localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function writeLS(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // localStorage can be disabled (private window, Safari ITP). Silently OK.
  }
}

export function App(): React.JSX.Element {
  const [endpoint, setEndpoint] = useState(() =>
    readLS(LS_KEYS.endpoint, DEFAULT_ENDPOINT),
  );
  const [apiKey, setApiKey] = useState(() => readLS(LS_KEYS.apiKey, ""));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BuildTxSuccessPayload | null>(null);
  /** Either a transport-level error string OR the parsed union-error payload. */
  const [error, setError] = useState<{
    readonly message: string;
    readonly raw?: unknown;
  } | null>(null);
  /** Last raw JSON response — always available for the "Raw" expander. */
  const [raw, setRaw] = useState<unknown>(null);
  /** Last raw JSON request body — mirror of `raw`, for the developer to reproduce via curl. */
  const [lastRequest, setLastRequest] = useState<unknown>(null);

  const [wallet, setWallet] = useState<WalletState | undefined>(undefined);
  /** Tx hashes broadcast by the StepsExecutor — drives the poller. */
  const [broadcastHashes, setBroadcastHashes] = useState<readonly Hex[]>([]);
  /**
   * Bump counter for the form's BalancePills. Increments every time the
   * StepsExecutor reports all steps as confirmed, so the pills refetch
   * `balanceOf` against the now-updated chain state without forcing a
   * full form reset.
   */
  const [balanceRefreshKey, setBalanceRefreshKey] = useState(0);
  /**
   * Bump-counter used as the `key` of QuoteForm so a reset remounts the
   * form back to its internal defaults without us having to lift every
   * field to App state. Endpoint and wallet are deliberately NOT reset
   * — endpoint is a developer setup choice, wallet is a separate
   * connect/disconnect flow. The API key IS reset because users
   * typically want to switch tenants on reset (and otherwise the cleared
   * form keeps polling the same catalog).
   */
  const [formKey, setFormKey] = useState(0);
  /**
   * Token pair that produced the current `result`. Kept in App-state so
   * `QuoteResult` can format amounts with the right decimals without
   * re-deriving the addresses from the response (which doesn't echo the
   * bridge token explicitly).
   */
  const [lastTokens, setLastTokens] = useState<{
    readonly fromToken: TokenDef;
    readonly toToken: TokenDef;
  } | null>(null);

  // Catalog is fetched per-tenant (apiKey scopes it). `chains` is empty until
  // `ready`; QuoteForm handles the empty case by disabling its selectors.
  const catalog = useCatalog(endpoint, apiKey);
  const chains = catalog.kind === "ready" ? catalog.chains : [];

  const onEndpointChange = useCallback((value: string) => {
    setEndpoint(value);
    writeLS(LS_KEYS.endpoint, value);
  }, []);
  const onApiKeyChange = useCallback((value: string) => {
    setApiKey(value);
    writeLS(LS_KEYS.apiKey, value);
  }, []);

  const onReset = useCallback(() => {
    setResult(null);
    setError(null);
    setRaw(null);
    setLastRequest(null);
    setBroadcastHashes([]);
    setLastTokens(null);
    setApiKey("");
    writeLS(LS_KEYS.apiKey, "");
    setFormKey((k) => k + 1);
  }, []);

  const onSubmit = useCallback(
    async (submission: QuoteSubmission) => {
      const { input, fromToken, toToken } = submission;
      setLoading(true);
      setResult(null);
      setError(null);
      setRaw(null);
      setLastRequest(null);
      // A fresh quote invalidates the previous broadcast set + token pair.
      setBroadcastHashes([]);
      setLastTokens(null);
      try {
        const { response, requestBody } = await gqlFetch<{
          readonly buildTx:
            | BuildTxSuccessPayload
            | {
                readonly __typename: string;
                readonly message: string;
                readonly field?: string;
              };
        }>({
          endpoint,
          apiKey,
          query: BUILD_TX_QUERY,
          operationName: "PlaygroundBuildTx",
          variables: { input },
        });
        setRaw(response);
        setLastRequest(requestBody);
        if (response.errors !== undefined && response.errors.length > 0) {
          setError({
            message: response.errors.map((e) => e.message).join(" · "),
            raw: response.errors,
          });
          return;
        }
        const payload = response.data?.buildTx;
        if (payload === undefined) {
          setError({
            message: "Empty response — check endpoint URL and CORS",
            raw: response,
          });
          return;
        }
        if (payload.__typename === "BuildTxSuccess") {
          setResult(payload as BuildTxSuccessPayload);
          setLastTokens({ fromToken, toToken });
          return;
        }
        const variantMessage =
          "message" in payload ? payload.message : "Unknown error variant";
        setError({
          message: `${payload.__typename}: ${variantMessage}`,
          raw: payload,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError({ message });
      } finally {
        setLoading(false);
      }
    },
    [endpoint, apiKey],
  );

  return (
    <main className={styles.shell}>
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>
            <span className={styles.brand}>swapgate</span>
            <span className={styles.divider}>·</span>
            <span className={styles.subtitle}>playground</span>
          </h1>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.resetBtn}
              onClick={onReset}
              title="Limpia formulario y resultados. Conserva endpoint, API key y wallet."
            >
              Reset form
            </button>
            <WalletConnect state={wallet} onChange={setWallet} />
          </div>
        </div>
        <p className={styles.tagline}>
          Quote calculator + sign &amp; send — `Query.buildTx` con EXACT_IN /
          EXACT_OUT, position BEFORE / AFTER, viem injected wallet.
        </p>
      </header>

      <EndpointBar
        endpoint={endpoint}
        apiKey={apiKey}
        onEndpointChange={onEndpointChange}
        onApiKeyChange={onApiKeyChange}
      />

      {catalog.kind === "loading" && (
        <small style={{ display: "block", opacity: 0.7, margin: "0.5rem 0" }}>
          Cargando catálogo…
        </small>
      )}
      {catalog.kind === "error" && (
        <small
          style={{ display: "block", color: "#c0392b", margin: "0.5rem 0" }}
          role="alert"
        >
          Error cargando catálogo: {catalog.message}
        </small>
      )}

      <QuoteForm
        key={formKey}
        chains={chains}
        loading={loading}
        wallet={wallet}
        balanceRefreshKey={balanceRefreshKey}
        onSubmit={onSubmit}
      />

      {error !== null && (
        <section className={styles.errorCard} role="alert">
          <strong>Error</strong>
          <p>{error.message}</p>
        </section>
      )}

      {result !== null && lastTokens !== null && (
        <QuoteResult
          result={result}
          fromToken={lastTokens.fromToken}
          toToken={lastTokens.toToken}
        />
      )}

      {result !== null && wallet !== undefined && (
        <StepsExecutor
          result={result}
          wallet={wallet}
          onCompleted={(hashes) => {
            setBroadcastHashes(hashes);
            // All steps confirmed → bump the balance pills so they refetch.
            setBalanceRefreshKey((k) => k + 1);
          }}
        />
      )}

      {result !== null && wallet === undefined && (
        <p className={styles.walletHint}>
          ↑ Conecta una wallet arriba para firmar y enviar los pasos del quote.
        </p>
      )}

      {broadcastHashes.length > 0 && result !== null && (
        <TxStatusPoller
          endpoint={endpoint}
          apiKey={apiKey}
          fromChainId={result.quote.fromChainId}
          toChainId={result.quote.toChainId}
          hashes={broadcastHashes}
        />
      )}

      {lastRequest !== null && (
        <details className={styles.rawCard}>
          <summary className={styles.rawSummary}>
            <span>Raw request</span>
            <CopyButton value={lastRequest} label="Copy JSON" />
          </summary>
          <pre>{JSON.stringify(lastRequest, null, 2)}</pre>
        </details>
      )}

      {raw !== null && (
        <details className={styles.rawCard}>
          <summary className={styles.rawSummary}>
            <span>Raw response</span>
            <CopyButton value={raw} label="Copy JSON" />
          </summary>
          <pre>{JSON.stringify(raw, null, 2)}</pre>
        </details>
      )}

      <footer className={styles.footer}>
        <span>V1.2 · presets + per-step sign &amp; track</span>
        <span className={styles.divider}>·</span>
        <span>Contratos parametrizables · receipt polling</span>
      </footer>
    </main>
  );
}
